<template>
  <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
  <div class="project-appointments" v-else-if="tableStateStore">
    <DataTable
      class="p-datatable-sm"
      :value="getAppointments"
      :loading="isLoading"
      :rowHover="true"
      :editMode="isReadOnly ? null : 'cell'"
      :lazy="true"
      filterDisplay="row"
      :filters.sync="tableStateStore.filters"
      stateStorage="local"
      :stateKey="tableStateStore.filterName()"
      :totalRecords="getAppointmentCount"
      :paginator="true"
      :rows.sync="tableStateStore.pagination.pageSize"
      :first="tableStateStore.skip()"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      @cell-edit-complete="onCellEditComplete"
      @page="onPage"
      @sort="onSort"
      @filter="onFilter"
      @state-restore="onStateRestore"
      :rowClass="rowClass"
      :disabled="!canWriteAppointments"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <div class="mt-2">
              {{ getAppointmentCount }}
              {{ $tc('appointment_pluralized', getAppointmentCount) }}
              <span
                v-if="
                  tableStateStore.filters.installationDateRange &&
                    tableStateStore.filters.installationDateRange.range &&
                    tableStateStore.filters.installationDateRange.range[0]
                "
              >
                im Zeitraum
                {{ $date(tableStateStore.filters.installationDateRange.range[0]) }}
                bis
                {{
                  tableStateStore.filters.installationDateRange.range[1]
                    ? $date(tableStateStore.filters.installationDateRange.range[1])
                    : 'N/A'
                }}
              </span>
            </div>
          </div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <div class="button-right">
              <button
                v-if="canCreateAppointments"
                class="btn btn-success mb-2 right"
                @click="onCreateAppointment"
              >
                <i class="fa fa-plus-circle mr-2"></i>
                {{ $t('new') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="appointmentType"
        :header="$t('appointmentType')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        headerClass="center project-appointments-column-narrow"
        className="center project-appointments-column-narrow"
        :showFilterMenu="false"
        sortable
      >
        <template #body="slotProps">
          <b-badge :variant="getAppointmentTypeColor(slotProps.data.appointmentType)">
            {{ $t(slotProps.data.appointmentType) }}
          </b-badge>
        </template>
        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="getEnumValues('AppointmentType')"
            :placeholder="$t('select')"
            :disabled="!canWriteAppointments"
          >
            <template #option="slotProps">
              <b-badge :variant="getAppointmentTypeColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
            <template #value="slotProps">
              <b-badge :variant="getAppointmentTypeColor(slotProps.value)">
                {{ $t(slotProps.value) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.appointmentTypes"
            :options="appointmentTypes"
            @input="onAppointmentTypeFilter"
            optionLabel="label"
            :placeholder="$t('appointmentType')"
            display="chip"
            class="project-appointments-enum-filter"
          />
        </template>
      </Column>

      <Column
        field="appointmentState"
        :header="$t('appointmentState')"
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center; width: 100px'"
        headerClass="center project-appointments-column-narrow"
        className="center project-appointments-column-narrow"
        :showFilterMenu="false"
        sortable
      >
        <template #body="slotProps">
          <b-badge :variant="getAppointmentStateColor(slotProps.data.appointmentState)">
            {{ $t(slotProps.data.appointmentState) }}
          </b-badge>
        </template>
        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="getEnumValues('AppointmentState')"
            :placeholder="$t('select')"
            :disabled="!canWriteAppointments"
          >
            <template #option="slotProps">
              <b-badge :variant="getAppointmentStateColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
            <template #value="slotProps">
              <b-badge :variant="getAppointmentStateColor(slotProps.value)">
                {{ $t(slotProps.value) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.appointmentStates"
            :options="appointmentStates"
            @input="onAppointmentStateFilter"
            optionLabel="label"
            :placeholder="$t('appointmentState')"
            display="chip"
            class="project-appointments-enum-filter"
          />
        </template>
      </Column>

      <Column
        field="appointmentApprovedState"
        :header="$t('appointmentApprovedState_short')"
        :styles="{ width: '7%' }"
        headerClass="center"
        className="center"
        :showFilterMenu="false"
        sortable
      >
        <template #body="slotProps">
          <b-badge :variant="getYesNoStateColor(slotProps.data.appointmentApprovedState)">
            {{ $t(slotProps.data.appointmentApprovedState) }}
          </b-badge>
        </template>
        <template #editor="{ data, field }">
          <Dropdown
            v-model="data[field]"
            :options="yesNo"
            :placeholder="$t('select')"
            :disabled="!canWriteAppointments"
          >
            <template #option="slotProps">
              <b-badge :variant="getYesNoStateColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
            <template #value="slotProps">
              <b-badge :variant="getYesNoStateColor(slotProps.value)">
                {{ $t(slotProps.value) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>
        <template #filter>
          <Dropdown
            :value="tableStateStore.customFilters.appointmentApprovedState.value"
            :options="yesNo"
            class="p-column-filter"
            :showClear="true"
            @input="onAppointmentApprovedStateFilter"
            :style="{ 'max-width': '100px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getYesNoStateColor(slotProps.option)">
                {{ $t(slotProps.option) }}
              </b-badge>
            </template>
          </Dropdown>
        </template>
      </Column>

      <Column
        field="installationStartAt"
        :header="$t('start')"
        :styles="{ width: '8%' }"
        headerClass="center appointment-date-range-filter-start"
        className="center"
        filterField="installationDateRange"
        :showFilterMenu="false"
        sortable
      >
        <template #body="slotProps">
          <strong>{{ $dayjs(slotProps.data[slotProps.column.field]).format('dddd') }}</strong
          ><br />
          {{ $datetime(slotProps.data[slotProps.column.field]) }}
        </template>
        <template #editor="slotProps">
          <Calendar
            v-model="slotProps.data[slotProps.column.field]"
            :showTime="true"
            :manualInput="true"
            :showWeek="true"
            :stepMinute="15"
            :disabled="!canWriteAppointments"
          >
          </Calendar>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <div class="appointment-date-range-filter-span">
            <Calendar
              v-model="filterModel.range"
              @input="filterCallback()"
              selectionMode="range"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date_range')"
            >
            </Calendar>
          </div>
        </template>
      </Column>
      <Column
        field="installationEndAt"
        :header="$t('end')"
        :styles="{ width: '8%' }"
        headerClass="center appointment-date-range-filter-end"
        className="center"
        :showFilterMenu="false"
        sortable
      >
        <template #body="slotProps">
          <strong>{{ $dayjs(slotProps.data[slotProps.column.field]).format('dddd') }}</strong
          ><br />
          {{ $datetime(slotProps.data[slotProps.column.field]) }}
        </template>
        <template #editor="slotProps">
          <Calendar
            v-model="slotProps.data[slotProps.column.field]"
            :showTime="true"
            :manualInput="true"
            :stepMinute="15"
            :disabled="!canWriteAppointments"
          >
          </Calendar>
        </template>
        <template #filter></template>
      </Column>

      <Column field="employees" :header="$t('employee')" sortable :showFilterMenu="false">
        <template #body="slotProps">
          <MultiselectEmployees
            v-if="!isReadOnly"
            v-model="slotProps.data.employees"
            :availableOn="slotProps.data.installationStartAt"
            @select="onSelectEmployee(slotProps)"
          />
          <div v-else-if="slotProps.data.employees && slotProps.data.employees.length">
            <div v-for="employee in slotProps.data.employees" :key="employee.number">
              {{ employee.firstname }} {{ employee.lastname }}
            </div>
          </div>
          <div v-else>-</div>
        </template>
        <template #filter>
          <InputText
            :value="tableStateStore.customFilters.employeeSearch.value"
            @input="onEmployeeSearch"
          />
        </template>
      </Column>

      <Column
        v-if="showsVehicles"
        field="itemStorages"
        :header="$t('vehicles')"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <multiselect
            v-if="!isReadOnly"
            class="multiselect-itemStorages"
            v-model="slotProps.data.itemStorages"
            :options="filteredItemStorages"
            track-by="number"
            v-on:search-change="onFilterItemStorages"
            v-on:select="onSelectItemStorage(slotProps)"
            v-on:remove="onSelectItemStorage(slotProps)"
            :filter="true"
            :internal-search="false"
            :close-on-select="false"
            :clear-on-select="false"
            :multiple="true"
            :show-labels="false"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :placeholder="'Fahrzeug ' + $t('select') + '/suchen'"
            :disabled="!canWriteAppointments"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }}
            </template>
            <template slot="option" slot-scope="{ option }"> {{ option.name }} </template>
            <template slot="tag" slot-scope="{ option }">
              <div>{{ option.name }}</div>
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
          <div v-else-if="slotProps.data.itemStorages && slotProps.data.itemStorages.length">
            <div v-for="itemStorage in slotProps.data.itemStorages" :key="itemStorage.number">
              {{ itemStorage.name }}
            </div>
          </div>
          <div v-else>-</div>
        </template>
        <template #filter>
          <InputText
            :value="tableStateStore.customFilters.itemStorageSearch.value"
            @input="onItemStorageSearch"
          />
        </template>
      </Column>

      <Column
        field="description"
        :header="$t('notes')"
        :styles="{ width: '15%' }"
        sortable
        :showFilterMenu="false"
      >
        <template #editor="slotProps">
          <InputText
            v-model="slotProps.data[slotProps.column.field]"
            :disabled="!canWriteAppointments"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column v-if="canManageAppointments" :showFilterMenu="false">
        <template #body="slotProps">
          <ConfirmButton
            colorVariant="white"
            :disabled="emailButtonDisabled(slotProps.data) || !canWriteAppointments"
            :callbackId="slotProps.data"
            :confirmCallback="onSendAppointmentConfirmationMail"
          >
            <template v-slot:buttonText>
              <i class="link-icon fa fa-lg fa-envelope mr-2"></i>
            </template>
            <template v-slot:header> {{ $t('confirm_appointment') }}? </template>
            <template v-slot:confirmButtonText>
              {{ $t('confirm_appointment') }}
            </template>
            <template v-slot:content>
              E-Mail-Bestätigung wird versendet an:
              <strong v-if="slotProps.data.project && slotProps.data.project.customer.email">
                {{ slotProps.data.project.customer.email }}</strong
              >
            </template>
          </ConfirmButton>
          <ConfirmButton
            colorVariant="white"
            :callbackId="slotProps.data.number"
            :confirmCallback="onDeleteAppointment"
            :disabled="!canWriteAppointments"
          >
            <template v-slot:buttonText>
              <i class="link-icon fa fa-lg fa-times mr-2"></i>
            </template>
            <template v-slot:header> {{ $t('delete_appointment') }}? </template>
            <template v-slot:confirmButtonText>
              {{ $t('delete_appointment') }}
            </template>
          </ConfirmButton>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { yesNo } from '@/helpers/enums';
import Calendar from 'primevue/calendar';
import {
  getAppointmentTypeColor,
  getAppointmentStateColor,
  getYesNoStateColor,
} from '../../helpers/colors';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { FilterMatchMode } from 'primevue/api';
import ConfirmButton from '@/components/ConfirmButton';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import MultiselectEmployees from '@/components/MultiselectEmployees';
import { canWriteField, showToGuest } from '@/helpers/userCapabilities';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

const createProjectAppointmentsDefaultFilters = () => ({
  description: { value: null },
  installationDateRange: { range: null },
});

const createProjectAppointmentsDefaultCustomFilters = () => ({
  appointmentTypes: [],
  appointmentStates: [],
  appointmentApprovedState: { value: 'YES' },
  employeeSearch: { value: null },
  itemStorageSearch: { value: null },
});

const mergeProjectAppointmentsCustomFilters = (defaults, current) => {
  const merged = { ...defaults };
  if (!current) {
    return merged;
  }

  merged.appointmentTypes = Array.isArray(current.appointmentTypes) ? current.appointmentTypes : [];
  merged.appointmentStates = Array.isArray(current.appointmentStates)
    ? current.appointmentStates
    : [];

  for (const key of ['appointmentApprovedState', 'employeeSearch', 'itemStorageSearch']) {
    if (current[key] && typeof current[key] === 'object' && 'value' in current[key]) {
      merged[key] = { ...defaults[key], ...current[key] };
    }
  }

  return merged;
};

/**
 * Block project-installation-appointments ("Installationstermine", see feature page
 * projekt-montage): the appointments of the project. Loads and writes through the paths of the
 * block (`projectNumber` in the appointment actions); the server fixes the project.
 *
 * No `BlockFieldset` root: a disabled fieldset would also disable the filters and the paging of
 * the table, which viewers need to read; every write control is covered by `isReadOnly`.
 */
export default {
  name: 'ProjectInstallationAppointments',
  mixins: [AccessBlockMixin],
  components: {
    Multiselect,
    Calendar,
    DataTable,
    Column,
    ConfirmButton,
    Dropdown,
    InputText,
    MultiSelect,
    MultiselectEmployees,
  },
  props: {
    /** The page project; the block data (the appointment list) is loaded by the block. */
    project: { type: Object, required: true },
  },
  data() {
    return {
      filteredItemStorages: [],
      projectAppointmentsTableStateConfig: {
        name: `project-appointments-${this.project.id}`,
        filters: createProjectAppointmentsDefaultFilters(),
        customFilters: createProjectAppointmentsDefaultCustomFilters(),
        sorting: { sortField: 'installationStartAt', sortOrder: -1 },
        pagination: { page: 0, pageSize: 20 },
      },
      guestWritableFields: {
        appointments: false,
      },
      guestVisibleSections: {
        createAppointment: false,
        canEditAppointment: false,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'isOrganizationRoleNavigationEnabled']),
    ...mapGetters([
      'getEnumValues',
      'isLoading',
      'getAppointments',
      'getTableState',
      'getAppointmentCount',
      'getItemStorages',
    ]),
    /** The block level: viewer reads, create, edit, mail and delete need editor. */
    isReadOnly() {
      return this.isBlockReadOnly;
    },
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    /** Guest rule only with the switch off; switched on the level decides, also for guests. */
    canWriteAppointments() {
      return (
        !this.isReadOnly &&
        (this.isOrganizationRoleNavigationEnabled ||
          canWriteField('appointments', this.guestWritableFields))
      );
    },
    canCreateAppointments() {
      return !this.isReadOnly && this.isGuestSectionVisible('createAppointment');
    },
    /** Mail and delete. */
    canManageAppointments() {
      return !this.isReadOnly && this.isGuestSectionVisible('canEditAppointment');
    },
    /** Column vehicles: every level with the switch on, admins only off. */
    showsVehicles() {
      return this.isOrganizationRoleNavigationEnabled || this.isAdmin;
    },

    tableStateStore() {
      return this.getTableState(this.projectAppointmentsTableStateConfig.name);
    },

    appointmentStates() {
      return this.getEnumValues('AppointmentState').map((appointmentState) => {
        return { value: appointmentState, label: this.$t(appointmentState) };
      });
    },

    appointmentTypes() {
      return this.getEnumValues('AppointmentType').map((appointmentType) => {
        return { value: appointmentType, label: this.$t(appointmentType) };
      });
    },

    yesNo,
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'initTableState',
      'fetchAppointmentPages',
      'createAppointment',
      'updateAppointment',
      'initAppointment',
      'fetchItemStoragesPaginated',
      'deleteAppointment',
      'sendAppointmentMail',
    ]),
    getYesNoStateColor,
    getAppointmentTypeColor,
    getAppointmentStateColor,
    /** Guest section rule, only with the switch off. */
    isGuestSectionVisible(section) {
      return (
        this.isOrganizationRoleNavigationEnabled || showToGuest(section, this.guestVisibleSections)
      );
    },
    async onSendAppointmentConfirmationMail(appointment) {
      await this.sendAppointmentMail({
        appointmentNumber: appointment.number,
        projectNumber: this.project.number,
      });
    },

    emailButtonDisabled(appointment) {
      return !(appointment.project && appointment.project.customer.email) ? true : false;
    },

    async save(appointment) {
      appointment.project = this.project;

      const blockPayload = { appointment, projectNumber: this.project.number };
      if (appointment.number === '_new') {
        await this.createAppointment(blockPayload);
      } else {
        await this.updateAppointment(blockPayload);
      }
    },

    async onSelectEmployee(row) {
      const { data } = row;
      await this.save(data);
    },

    onFilterItemStorages(query) {
      this.filteredItemStorages = [];
      this.getItemStorages.map((itemStorage) => {
        if (itemStorage.name && itemStorage.name.toLowerCase().indexOf(query) >= 0) {
          this.filteredItemStorages.push(itemStorage);
        }
      });
    },

    async onSelectItemStorage(row) {
      const { data } = row;
      await this.save(data);
    },

    async onCellEditComplete(event) {
      let { data, newValue, field } = event;

      if (field === 'installationStartAt') {
        data.weekday = this.$dayjs(newValue)
          .startOf('day')
          .format('dddd DD.MM.YYYY');

        data.installationEndAt = this.$dayjs(newValue)
          .add(8, 'hour')
          .toDate();
      }

      data[field] = newValue;
      await this.save(data);
    },

    onCreateAppointment() {
      this.initAppointment();
    },

    onDeleteAppointment(appointmentNumber) {
      this.deleteAppointment({ appointmentNumber, projectNumber: this.project.number });
    },

    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    async loadTableData() {
      if (this.project.id === '_new') {
        return;
      }

      // the server forces the project of the block
      const filters = {
        ...this.tableStateStore.filters,
        ...this.tableStateStore.customFilters,
      };

      await this.fetchAppointmentPages({
        page: this.tableStateStore.skip(),
        pageSize: this.tableStateStore.pagination.pageSize,
        sortField: this.tableStateStore.sorting.sortField,
        sortOrder: this.tableStateStore.sorting.sortOrder,
        filters,
        projectNumber: this.project.number,
      });
    },

    onFilter() {
      const installationDateRange = this.tableStateStore.filters.installationDateRange;
      if (installationDateRange && installationDateRange.range && installationDateRange.range[1]) {
        installationDateRange.range[1] = this.$dayjs(installationDateRange.range[1])
          .endOf('day')
          .toDate();
      }

      this.loadTableData();
    },

    onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    ensureTableFilters() {
      if (!this.tableStateStore) {
        return;
      }

      this.tableStateStore.customFilters = mergeProjectAppointmentsCustomFilters(
        createProjectAppointmentsDefaultCustomFilters(),
        this.tableStateStore.customFilters
      );
    },

    onStateRestore(event) {
      this.tableStateStore.restore(event);
      this.ensureTableFilters();
      this.loadTableData();
    },

    onRemoveFilters() {
      const defaultFilters = createProjectAppointmentsDefaultFilters();
      const defaultCustomFilters = createProjectAppointmentsDefaultCustomFilters();

      this.projectAppointmentsTableStateConfig.filters = defaultFilters;
      this.projectAppointmentsTableStateConfig.customFilters = defaultCustomFilters;

      this.tableStateStore.reset();
      this.tableStateStore.filters = defaultFilters;
      this.tableStateStore.customFilters = defaultCustomFilters;

      this.loadTableData();
    },

    async onAppointmentTypeFilter(value) {
      this.tableStateStore.setCustomFilters({
        appointmentTypes: this.appointmentTypes.filter((appointmentType) =>
          value.includes(appointmentType)
        ),
      });
      await this.loadTableData();
    },

    async onAppointmentStateFilter(value) {
      this.tableStateStore.setCustomFilters({
        appointmentStates: this.appointmentStates.filter((appointmentState) =>
          value.includes(appointmentState)
        ),
      });
      await this.loadTableData();
    },

    async onAppointmentApprovedStateFilter(value) {
      this.tableStateStore.setCustomFilters({
        appointmentApprovedState: { value },
      });
      await this.loadTableData();
    },

    async onEmployeeSearch(value) {
      this.tableStateStore.setCustomFilters({
        employeeSearch: { value },
      });
      await this.loadTableData();
    },

    async onItemStorageSearch(value) {
      this.tableStateStore.setCustomFilters({
        itemStorageSearch: { value },
      });
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.projectAppointmentsTableStateConfig);
    this.ensureTableFilters();
  },

  async mounted() {
    if (this.isNewProject) {
      return;
    }
    await this.fetchEnumValues('AppointmentType');
    await this.fetchEnumValues('AppointmentState');
    // the vehicles to assign; only an editor assigns them
    if (!this.isReadOnly) {
      await this.fetchItemStoragesPaginated({
        pagination: { skip: 0, pageSize: 100 },
        sorting: { sortField: 'number', sortOrder: 1 },
        filters: { itemStorageType: { value: 'VEHICLE', matchMode: FilterMatchMode.CONTAINS } },
      });
      this.filteredItemStorages = this.getItemStorages;
    }
    this.ensureTableFilters();
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .center .p-column-header-content {
  justify-content: center;
}

/** Span installationDateRange filter across start + end columns (PrimeVue has no filter colspan) */
.appointment-date-range-filter-span {
  position: relative;
  z-index: 2;
  min-width: 1rem;
  width: calc(200% + 0.5rem);
}

::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}

::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}

::v-deep .p-datatable .p-datatable-tbody td {
  text-align: left;
}
::v-deep .p-datatable .p-datatable-tbody td.center {
  text-align: center;
}
::v-deep .p-datatable .p-datatable-tbody > tr.new {
  background: $light-green;
}
::v-deep .p-datatable .p-datatable-tbody > tr.new:hover {
  background: $green;
}

::v-deep tr.p-rowgroup-header {
  background: $gray-100;
  span {
    font-weight: 700;
  }
  .p-row-toggler {
    vertical-align: middle;
    margin-right: 0.25rem;
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

::v-deep .project-appointments-enum-filter.p-multiselect {
  width: 100%;
  max-width: 100px;
  min-width: 0;
}

::v-deep .project-appointments-column-narrow .p-column-filter {
  max-width: 100px;
}

.multiselect-itemStorages,
.multiselect-employees {
  min-width: 230px;
  display: inline-block;
}

::v-deep .multiselect__tags {
  border: 0;
  border-radius: 0;
}

::v-deep .multiselect-employees .multiselect__input {
  padding-top: 1em;
}

/** Fix Dropdown overlay */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
